import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuthContext } from "./useUserAuthContext";
import API_URL from "../api/API";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, dispatch } = useUserAuthContext();
  const profile = user?.user;

  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  const [openCourseId, setOpenCourseId] = useState(null);
  const [openStepId, setOpenStepId] = useState(null);

  const userInitial = profile?.name?.trim()?.[0]?.toUpperCase() || "U";

  const handleLogout = () => {
    localStorage.removeItem("user");
    dispatch({ type: "LOGOUT" });
    navigate("/");
  };

  useEffect(() => {
    const loadEnrollments = async () => {
      if (!user?.token) return;
      setLoading(true);
      try {
        const res = await fetch(`${API_URL.FRONTEND_GET_ENROLLMENTS}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        const data = await res.json();
        if (res.ok && data.status) {
          setEnrollments(data.enrollments || []);
        }
      } catch (err) {
        console.error("Failed to load enrollments:", err);
      } finally {
        setLoading(false);
      }
    };
    loadEnrollments();
  }, [user?.token]);

  if (!user || !profile || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-zinc-700">
        Loading...
      </div>
    );
  }

  const MAX_POINTS_PER_STEP = 10;

  const toggleCourse = (courseId) => {
    setOpenCourseId(openCourseId === courseId ? null : courseId);
    setOpenStepId(null); // close any open step when changing course
  };

  const toggleStep = (stepIndex) => {
    setOpenStepId(openStepId === stepIndex ? null : stepIndex);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-zinc-900 py-8 px-4 sm:px-6 lg:px-8">
      {/* Top Bar */}
      <div className="mx-auto max-w-3xl flex items-center justify-between">
        <div>
          <div className="text-2xl font-bold text-indigo-700">SoulWorkout</div>
          <div className="text-xs text-zinc-500">Dashboard</div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/home")}
            className="rounded-xl border border-indigo-700 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 transition"
          >
            Home
          </button>

          <button
            onClick={handleLogout}
            className="rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-800 hover:bg-zinc-50 transition"
          >
            Logout
          </button>
        </div>
      </div>

      {/* User Info */}
      <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm flex items-center gap-4">
        <div className="h-14 w-14 overflow-hidden rounded-full border border-zinc-200 bg-zinc-100">
          {profile.picture && !imgError ? (
            <img
              src={profile.picture}
              alt="Profile"
              className="h-full w-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm font-semibold text-zinc-600">
              {userInitial}
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="text-base font-semibold text-zinc-900">{profile.name || "Unnamed"}</div>
          <div className="text-sm text-zinc-600">{profile.email || "-"}</div>
        </div>
      </div>

      {/* Courses Accordion */}
      <div className="mx-auto mt-6 max-w-3xl space-y-4">
        {enrollments.length === 0 && (
          <div className="text-sm text-zinc-600 text-center">
            You have no courses yet.
          </div>
        )}

        {enrollments.map((enrollment, idx) => {
          const workout = enrollment.workoutMaster || {};
          const courseCompleted = enrollment.completed || false;

          const totalPoints =
            enrollment.steps?.reduce((acc, s) => acc + (s.pointsEarned || 0), 0) || 0;
          const totalMaxPoints = (enrollment.steps?.length || 0) * MAX_POINTS_PER_STEP;
          const percentileScore =
            totalMaxPoints > 0 ? Math.round((totalPoints / totalMaxPoints) * 100) : 0;

          const badgesEarned =
            enrollment.steps?.map((s) => s.badgeEarned).filter(Boolean) || [];

          const isCourseOpen = openCourseId === enrollment._id;

          return (
            <div key={enrollment._id || idx} className="rounded-2xl border border-indigo-200 shadow-sm bg-indigo-50">
              {/* Course Header */}
              <div
                className="flex justify-between items-center p-4 cursor-pointer hover:bg-indigo-100 transition"
                onClick={() => toggleCourse(enrollment._id)}
              >
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="text-indigo-700 text-lg">{isCourseOpen ? "▼" : "▶"}</span>
                    <div className="text-lg font-bold text-indigo-800">{workout.title || "Untitled Course"}</div>
                  </div>
                  <div className="text-xs text-zinc-600 mt-1">
                    {enrollment.steps?.length || 0} Steps | Percentile: {percentileScore}%
                  </div>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    courseCompleted ? "bg-green-600 text-white" : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {courseCompleted ? "Completed" : "In Progress"}
                </span>
              </div>

              {/* Course Body */}
              {isCourseOpen && (
                <div className="p-4 border-t border-indigo-200 bg-white space-y-4">
                  {enrollment.steps?.map((step) => {
                    const statusColors = {
                      completed: "bg-green-500 text-white",
                      slipped: "bg-red-500 text-white",
                      skipped: "bg-yellow-400 text-zinc-900",
                      pending: "bg-zinc-200 text-zinc-800",
                    };
                    const statusIcons = {
                      completed: "✓",
                      slipped: "⚠️",
                      skipped: "⏭",
                      pending: "•",
                    };
                    const stepStatus = step.status || "pending";
                    const isStepOpen = openStepId === step.workoutIndex;

                    return (
                      <div key={step.workoutIndex} className="rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
                        {/* Step Header */}
                        <div
                          className="flex justify-between items-center p-3 cursor-pointer hover:bg-gray-50 transition"
                          onClick={() => toggleStep(step.workoutIndex)}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${statusColors[stepStatus]}`}>
                              {isStepOpen ? "▼" : "▶"}
                            </span>
                            <span className="text-sm font-semibold text-zinc-900">
                              Step {step.workoutIndex}: {step.title || step.topic || "Step"}
                            </span>
                          </div>
                          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold shadow-sm ${statusColors[stepStatus]}`}>
                            {statusIcons[stepStatus]} {stepStatus.charAt(0).toUpperCase() + stepStatus.slice(1)}
                          </span>
                        </div>

                        {/* Step Details */}
                        {isStepOpen && (
                          <div className="p-3 border-t border-zinc-200 text-sm text-zinc-700 space-y-2">
                            {step.captureValue && (
                              <div>
                                <div className="font-semibold">Response:</div>
                                <div>{JSON.stringify(step.captureValue)}</div>
                              </div>
                            )}
                            <div>Points: {step.pointsEarned || 0}</div>
                            {step.badgeEarned && (
                              <div className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs font-semibold shadow-sm">
                                🏅 {step.badgeEarned}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Course Completion Summary */}
                  {courseCompleted && (
                    <div className="mt-4 rounded-2xl border border-green-200 bg-green-50 p-4 shadow-sm">
                      <div className="text-lg font-bold text-green-900">🎉 Journey Completed!</div>
                      <div className="mt-1 text-sm text-green-800">
                        Completed on: {enrollment.completedAt ? new Date(enrollment.completedAt).toLocaleDateString() : "-"}
                      </div>
                      <div className="mt-2 text-sm text-green-900 font-semibold">Total Points: {totalPoints}</div>
                      {badgesEarned.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2 overflow-x-auto">
                          {badgesEarned.map((b, i) => (
                            <span
                              key={i}
                              className="flex-shrink-0 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800"
                            >
                              {b}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mx-auto max-w-3xl mt-10 border-t border-zinc-200 pt-6 text-center text-xs text-zinc-500">
        © {new Date().getFullYear()} SoulWorkout • Built for daily reform
      </div>
    </div>
  );
}